// context/SurveyContext.js — shared state for Staff Surveys (admin manager + staff portal)
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/msalService";
import { useUserAccess } from "./UserAccessContext";
import {
  fetchSurveyDb, createSurvey, updateSurvey, deleteSurveyItem,
  createAssignment, deleteAssignmentItem, createResponse, deleteResponseItem,
  loadLocalSurveyDb, saveLocalSurveyDb, sendSurveyEmails, generateId,
} from "../services/surveyService";
import { PROVISIONED_EVENT } from "../services/provisioningService";
import { cloneQuestions, assignmentStatus } from "../components/Survey/surveyUtils";

const SurveyContext = createContext(null);

export function useSurveys() {
  const ctx = useContext(SurveyContext);
  if (!ctx) throw new Error("useSurveys must be used inside SurveyProvider");
  return ctx;
}

const EMPTY_DB = { surveys: [], assignments: [], responses: [] };

export function SurveyProvider({ children }) {
  const { instance, accounts } = useMsal();
  // Survey storage follows the user-access connection: demo mode → localStorage,
  // otherwise SharePoint only, so a staff member never silently writes to their browser.
  const { isDemoMode, loading: accessLoading, currentUserEmail, currentUserName, currentUser } = useUserAccess();
  const [db, setDb] = useState(EMPTY_DB);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const me = (currentUserEmail || "").toLowerCase();

  const load = useCallback(async () => {
    if (accessLoading) return;
    setLoading(true);
    setError(null);
    if (isDemoMode) {
      setDb(loadLocalSurveyDb());
      setLoading(false);
      return;
    }
    try {
      const token = await getAccessToken(instance, accounts);
      setDb(await fetchSurveyDb(token));
    } catch (err) {
      console.error("[Surveys] SharePoint load failed:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessLoading, isDemoMode, instance, accounts]);

  useEffect(() => { load(); }, [load]);

  // Lists may have just been created by the admin auto-provisioner
  useEffect(() => {
    window.addEventListener(PROVISIONED_EVENT, load);
    return () => window.removeEventListener(PROVISIONED_EVENT, load);
  }, [load]);

  // Apply a change to state, persisting the whole db in demo mode
  const commit = useCallback((fn) => {
    setDb((prev) => {
      const next = fn(prev);
      if (isDemoMode) saveLocalSurveyDb(next);
      return next;
    });
  }, [isDemoMode]);

  const token = useCallback(() => getAccessToken(instance, accounts), [instance, accounts]);

  // ── Indexes ────────────────────────────────────────────────────────────────

  const surveyById = useMemo(() => Object.fromEntries(db.surveys.map((s) => [s.id, s])), [db.surveys]);

  const responseByAssignment = useMemo(() => {
    const map = {};
    // Oldest wins, matching selectResponses' de-duplication
    [...db.responses].sort((a, b) => (a.submittedAt < b.submittedAt ? -1 : 1))
      .forEach((r) => { if (!map[r.assignmentId]) map[r.assignmentId] = r; });
    return map;
  }, [db.responses]);

  const getSurveyData = useCallback((surveyId) => ({
    survey: surveyById[surveyId] || null,
    assignments: db.assignments.filter((a) => a.surveyId === surveyId),
    responses: db.responses.filter((r) => r.surveyId === surveyId),
  }), [surveyById, db.assignments, db.responses]);

  const myAssignments = useMemo(() => db.assignments
    .filter((a) => a.email === me && surveyById[a.surveyId])
    .map((a) => {
      const survey = surveyById[a.surveyId];
      const response = responseByAssignment[a.id] || null;
      return { ...a, survey, response, status: assignmentStatus(a, response, survey) };
    })
    .sort((a, b) => (a.sharedAt < b.sharedAt ? 1 : -1)), [db.assignments, me, surveyById, responseByAssignment]);

  const myPendingCount = myAssignments.filter((a) => a.status === "pending" || a.status === "overdue").length;

  // ── Surveys ────────────────────────────────────────────────────────────────

  const saveSurvey = useCallback(async (draft) => {
    const now = new Date().toISOString();
    const existing = draft.id ? surveyById[draft.id] : null;
    const survey = {
      ...existing,
      ...draft,
      status: draft.status || existing?.status || "draft",
      createdBy: existing?.createdBy || me,
      createdByName: existing?.createdByName || currentUserName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };
    if (isDemoMode) {
      const saved = existing ? survey : { ...survey, id: generateId("sv") };
      commit((d) => ({ ...d, surveys: existing ? d.surveys.map((s) => (s.id === saved.id ? saved : s)) : [saved, ...d.surveys] }));
      return saved;
    }
    const t = await token();
    if (existing) {
      await updateSurvey(t, survey);
      commit((d) => ({ ...d, surveys: d.surveys.map((s) => (s.id === survey.id ? survey : s)) }));
      return survey;
    }
    const created = await createSurvey(t, survey);
    commit((d) => ({ ...d, surveys: [created, ...d.surveys] }));
    return created;
  }, [surveyById, me, currentUserName, isDemoMode, commit, token]);

  const setSurveyStatus = useCallback((id, status) => saveSurvey({ ...surveyById[id], status }), [saveSurvey, surveyById]);

  const duplicateSurvey = useCallback((id) => {
    const src = surveyById[id];
    return saveSurvey({
      title: `${src.title} (copy)`,
      description: src.description,
      category: src.category,
      settings: { ...src.settings, closesAt: "" },
      questions: cloneQuestions(src.questions),
      status: "draft",
    });
  }, [surveyById, saveSurvey]);

  const deleteSurvey = useCallback(async (id) => {
    const { survey, assignments, responses } = getSurveyData(id);
    if (!isDemoMode) {
      const t = await token();
      for (const r of responses) await deleteResponseItem(t, r);
      for (const a of assignments) await deleteAssignmentItem(t, a);
      await deleteSurveyItem(t, survey);
    }
    commit((d) => ({
      surveys: d.surveys.filter((s) => s.id !== id),
      assignments: d.assignments.filter((a) => a.surveyId !== id),
      responses: d.responses.filter((r) => r.surveyId !== id),
    }));
  }, [getSurveyData, isDemoMode, token, commit]);

  // ── Sharing ────────────────────────────────────────────────────────────────

  /**
   * Share with recipients [{ email, name, department }].
   * Anyone still holding an unanswered assignment is skipped (they can already respond);
   * anyone who has answered gets a new round, which lets them respond again.
   */
  const shareSurvey = useCallback(async (surveyId, recipients, { dueDate = "", notify = false, message = "", onProgress } = {}) => {
    const survey = surveyById[surveyId];
    const existing = db.assignments.filter((a) => a.surveyId === surveyId);
    const now = new Date().toISOString();
    const created = [];
    const skipped = [];
    const failed = [];

    const unique = [...new Map(recipients.map((r) => [r.email.trim().toLowerCase(), r])).values()];
    for (let i = 0; i < unique.length; i++) {
      const r = unique[i];
      const email = r.email.trim().toLowerCase();
      const mine = existing.filter((a) => a.email === email);
      if (mine.some((a) => !responseByAssignment[a.id])) { skipped.push(r); continue; }
      const assignment = {
        id: generateId("as"),
        surveyId,
        email,
        name: r.name || email,
        department: r.department || "",
        round: mine.reduce((m, a) => Math.max(m, a.round), 0) + 1,
        sharedBy: me,
        sharedByName: currentUserName,
        sharedAt: now,
        dueDate,
      };
      try {
        created.push(isDemoMode ? assignment : await createAssignment(await token(), assignment));
      } catch (err) {
        failed.push({ email, error: err.message });
      }
      onProgress?.("share", i + 1, unique.length);
    }
    if (created.length) commit((d) => ({ ...d, assignments: [...created, ...d.assignments] }));

    // Sharing a draft publishes it
    if (survey.status !== "active" && created.length) await saveSurvey({ ...survey, status: "active" });

    let email = { sent: 0, failed: [] };
    if (notify && created.length) {
      try {
        email = await sendSurveyEmails(instance, accounts, {
          survey, message, dueDate, senderName: currentUserName,
          recipients: created.map((a) => ({ email: a.email, name: a.name, assignmentId: a.id })),
          onProgress: (n, total) => onProgress?.("email", n, total),
        });
      } catch (err) {
        email = { sent: 0, failed: created.map((a) => ({ email: a.email, error: err.message })) };
      }
    }
    return { created, skipped, failed, email };
  }, [surveyById, db.assignments, responseByAssignment, me, currentUserName, isDemoMode, token, commit, saveSurvey, instance, accounts]);

  const sendReminders = useCallback(async (surveyId, assignmentIds, { message = "", onProgress } = {}) => {
    const survey = surveyById[surveyId];
    const targets = db.assignments.filter((a) => assignmentIds.includes(a.id) && !responseByAssignment[a.id]);
    if (!targets.length) return { sent: 0, failed: [] };
    return sendSurveyEmails(instance, accounts, {
      survey, message, isReminder: true, senderName: currentUserName,
      dueDate: targets[0].dueDate,
      recipients: targets.map((a) => ({ email: a.email, name: a.name, assignmentId: a.id })),
      onProgress,
    });
  }, [surveyById, db.assignments, responseByAssignment, instance, accounts, currentUserName]);

  const revokeAssignments = useCallback(async (assignmentIds) => {
    const targets = db.assignments.filter((a) => assignmentIds.includes(a.id) && !responseByAssignment[a.id]);
    if (!isDemoMode) {
      const t = await token();
      for (const a of targets) await deleteAssignmentItem(t, a);
    }
    const ids = new Set(targets.map((a) => a.id));
    commit((d) => ({ ...d, assignments: d.assignments.filter((a) => !ids.has(a.id)) }));
    return targets.length;
  }, [db.assignments, responseByAssignment, isDemoMode, token, commit]);

  // ── Responding ─────────────────────────────────────────────────────────────

  const submitResponse = useCallback(async (assignmentId, answers, durationSec) => {
    const assignment = db.assignments.find((a) => a.id === assignmentId);
    if (!assignment) throw new Error("This survey link is no longer valid.");
    if (assignment.email !== me) throw new Error("This survey was shared with a different account.");
    if (responseByAssignment[assignmentId]) throw new Error("You have already submitted this survey.");
    const response = {
      id: generateId("rs"),
      surveyId: assignment.surveyId,
      assignmentId,
      email: me,
      name: currentUserName,
      department: currentUser?.department || assignment.department || "",
      round: assignment.round,
      answers,
      submittedAt: new Date().toISOString(),
      durationSec: Math.round(durationSec || 0),
    };
    const saved = isDemoMode ? response : await createResponse(await token(), response);
    commit((d) => ({ ...d, responses: [...d.responses, saved] }));
    return saved;
  }, [db.assignments, me, responseByAssignment, currentUserName, currentUser, isDemoMode, token, commit]);

  const value = {
    ...db,
    loading: loading || accessLoading,
    error,
    isDemoMode,
    refresh: load,
    surveyById,
    responseByAssignment,
    getSurveyData,
    myAssignments,
    myPendingCount,
    saveSurvey,
    setSurveyStatus,
    duplicateSurvey,
    deleteSurvey,
    shareSurvey,
    sendReminders,
    revokeAssignments,
    submitResponse,
  };

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
}
